import { useRef, useEffect, useState, useMemo, useCallback, useLayoutEffect } from "react";
import { ChatList } from "./ChatList";
import { SearchInput } from "./ChatInput";
import { useChat } from "../context/ChatContext";
import type { Product } from '../lib/mockData';
import type { ChatMessage as ChatMessageType } from "../lib/types";
import { ShoppingListButton } from "./ShoppingListButton";
import { chatLogger } from "../lib/logger";
import { useLocale } from "../context/LocaleContext";

interface ChatInterfaceProps {
  onProductSelect?: (product: Product) => void;
}

export function ChatInterface({ onProductSelect }: ChatInterfaceProps) {
  const { t } = useLocale();
  const anyChat: any = useChat();
  const { messages, loading, sendMessage, selectOption } = anyChat;
  const sessionId = (anyChat as any).sessionId as string | null;
  const status = (anyChat as any).status as { title?: string; status?: string } | null;
  const { shoppingList } = anyChat;
  const staticPrefix = (anyChat as any).staticPrefix as string | undefined;
  const performGuidedAction = (anyChat as any).performGuidedAction as ((a: any) => void) | undefined;
  const systemActions = (anyChat as any).lastSystemActions as any[] | null;
  const [showThinking, setShowThinking] = useState(false);
  const bottomBarRef = useRef<HTMLDivElement | null>(null);
  const [bottomBarHeight, setBottomBarHeight] = useState(0);
  // Deprecated anchoring system removed in Phase 1
  const showPromptActions = staticPrefix === "prompt.prefix_answer" && Array.isArray(systemActions) && systemActions.length > 0;
  // Removed anchoring-related refs in Phase 1

  // Phase 2: Simplified anchoring - track container and last user element
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastUserElRef = useRef<HTMLElement | null>(null);
  const lastAnchoredUserIdRef = useRef<string | null>(null);
  const [bottomSpacerPx, setBottomSpacerPx] = useState(0);
  const EXTRA_MARGIN_PX = 16;
  const loaderRef = useRef<HTMLDivElement | null>(null);

  // Scoped logger that writes to console and, if a session exists, to chatLogger
  const log = useCallback((message: string, metadata?: Record<string, any>) => {
    try { console.log('[ChatAnchor]', message, metadata ?? ''); } catch {}
    if (sessionId) {
      try { chatLogger.log({ type: 'SYSTEM', message: `Anchor: ${message}`, metadata }); } catch {}
    }
  }, [sessionId]);

  // Compute the real last user message id
  const lastUserMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i] as any;
      if (m && m.isUser) return m.id as string;
    }
    return null;
  }, [messages]);

  useEffect(() => {
    log('lastUserMessageId updated', { lastUserMessageId, messagesLen: messages.length });
  }, [lastUserMessageId, messages.length, log]);

  // Anchor last user to top without animation
  const scrollLastUserToTop = useCallback(() => {
    const container = containerRef.current;
    const el = lastUserElRef.current;
    if (!container || !el) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const targetTop = eRect.top - cRect.top + container.scrollTop;
    const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
    const nextTop = Math.min(Math.max(targetTop, 0), maxTop);
    log('scrollLastUserToTop', { targetTop, nextTop, maxTop });
    const prevBehavior = container.style.scrollBehavior;
    container.style.scrollBehavior = 'auto';
    container.scrollTo({ top: nextTop, behavior: 'auto' });
    container.style.scrollBehavior = prevBehavior;
  }, [log]);

  // Optional: minimal loader anchoring when options/guided actions are clicked
  const scrollLoaderToTop = useCallback(() => {
    const c = containerRef.current, el = loaderRef.current;
    if (!c || !el) return;
    const t = el.getBoundingClientRect().top - c.getBoundingClientRect().top + c.scrollTop;
    const maxTop = Math.max(0, c.scrollHeight - c.clientHeight);
    const nextTop = Math.min(Math.max(t, 0), maxTop);
    log('scrollLoaderToTop', { targetTop: t, nextTop, maxTop });
    c.scrollTo({ top: nextTop, behavior: 'smooth' });
  }, [log]);

  // Compute minimal bottom spacer so the last user can reach the top
  const recomputeBottomSpacer = useCallback(() => {
    const c = containerRef.current;
    const el = lastUserElRef.current;
    if (!c || !el) { setBottomSpacerPx(0); return; }
    const cRect = c.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const anchorTop = eRect.top - cRect.top + c.scrollTop;
    const needed = Math.max(0, anchorTop + c.clientHeight - c.scrollHeight + EXTRA_MARGIN_PX);
    setBottomSpacerPx(needed);
    log('recomputeBottomSpacer', { anchorTop, clientHeight: c.clientHeight, scrollHeight: c.scrollHeight, needed });
  }, [log]);

  // When last user or overlay height changes, compute spacer (EFFECT 1)
  useLayoutEffect(() => {
    if (!lastUserMessageId) { setBottomSpacerPx(0); return; }
    recomputeBottomSpacer();
  }, [lastUserMessageId, bottomBarHeight, recomputeBottomSpacer]);

  // When the spacer is ready, anchor the last user message (EFFECT 2)
  useLayoutEffect(() => {
    if (!lastUserMessageId) return;
    if (lastAnchoredUserIdRef.current === lastUserMessageId) return;
    
    // Check if there is enough room before scrolling.
    const c = containerRef.current;
    const el = lastUserElRef.current;
    if (!c || !el) return;
    const targetTop = el.getBoundingClientRect().top - c.getBoundingClientRect().top + c.scrollTop;
    const maxTop = Math.max(0, c.scrollHeight - c.clientHeight);

    if (maxTop >= targetTop) {
      log('anchoring last user to top', { lastUserMessageId });
      scrollLastUserToTop();
      lastAnchoredUserIdRef.current = lastUserMessageId;
    }
  }, [lastUserMessageId, bottomSpacerPx, scrollLastUserToTop, log]);

  // Keep spacer fresh on container resize
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    const ro = new ResizeObserver(() => recomputeBottomSpacer());
    ro.observe(c);
    return () => ro.disconnect();
  }, [recomputeBottomSpacer]);

  // Determine if the latest assistant message is showing guided result actions
  const lastAssistantMessage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i] as any;
      if (!m.isUser) return m;
    }
    return null;
  }, [messages]);

  const hasGuidedResultActions = useMemo(() => {
    if (!lastAssistantMessage) return false;
    const actions = (lastAssistantMessage as any)?.actions;
    if (!Array.isArray(actions) || actions.length === 0) return false;
    return actions.some((a: any) => a?.template === 'REFINE' || a?.template === 'NEW_SEARCH' || a?.template === 'QUESTION_PRODUCTS');
  }, [lastAssistantMessage]);

  // When a guided action is staged, input should become enabled for user text
  const isStagedForResult = (
    staticPrefix === "prefix.refine" ||
    staticPrefix === "prefix.new_search" ||
    staticPrefix === "prefix.question"
  );
  const shouldDisableGuided = hasGuidedResultActions && !isStagedForResult;
  const computedStaticPrefixKey = shouldDisableGuided ? "guided.choose_above" : staticPrefix;

  // Static, always-first assistant intro (render-time only; not persisted to state)
  const introMessage: ChatMessageType = useMemo(() => ({
    id: "static-intro",
    isUser: false,
    text: [
      "Välkommen! Här kan du få hjälp med att hitta rätt produkter, ställa frågor eller jämföra. Om jag ställer en fråga du inte förstår så kan du alltid fråga om förtydligande. Formulera dig hur du vill, precis som du hade gjort i butiken. Skriv in vad du letar efter så hittar vi det tillsammans.",
    ].join("\n"),
  }), []);

  const displayMessages = useMemo(() => [introMessage, ...messages], [messages, introMessage]);

  // Disable the old bottom scrolling. We will explicitly control scroll on user actions.

  useEffect(() => {
    let timer: number;
    if (loading) {
      timer = window.setTimeout(() => {
        setShowThinking(true);
      }, 2000);
    } else {
      setShowThinking(false);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [loading]);

  // Keep scroll area's bottom padding in sync with overlay height
  useEffect(() => {
    const el = bottomBarRef.current;
    if (!el) return;
    const update = () => setBottomBarHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Removed anchoring effects and tail-space computations in Phase 1

  const handleSubmit = (value: string) => {
    if (value.trim().length === 0) return;
    log('onSubmit', { valueLen: value.length, messagesLenBefore: messages.length });
    sendMessage(value);
    // Anchoring removed in Phase 1
  };

  const handleOptionClick = (options: Array<{ index: number; label: string }>) => {
    if (!options || options.length === 0) return;
    log('onOptionClick', { options });
    selectOption(options);
    // Optional: align loader at top on next tick
    setTimeout(scrollLoaderToTop, 0);
  };

  // Removed anchor callback handlers in Phase 1

  return (
    <div className="relative max-w-[1400px] flex flex-col h-full bg-[var(--color-background)] overflow-hidden px-5">
      <div className="flex-1 overflow-y-auto" ref={containerRef} style={{ overscrollBehavior: 'contain', paddingBottom: bottomBarHeight + 16 }}>
        <ChatList 
          messages={displayMessages}
          onProductSelect={onProductSelect}
          onOptionSelect={handleOptionClick}
          onActionSelect={(a) => performGuidedAction?.(a)}
          lastUserMessageId={lastUserMessageId}
          setUserAnchor={(el) => { lastUserElRef.current = el; log('setUserAnchor', { assigned: Boolean(el), lastUserMessageId }); }}
        />
        {loading && (
          <div className="flex justify-start px-8 py-2">
            <div
              className="px-4 py-2 rounded-3xl inline-flex items-center"
              ref={loaderRef}
            >
              <span className="dot-spinner" />
              <div
                key={`${status?.title ?? ''}::${status?.status ?? ''}`}
                className="ml-[12px] text-left animate-in fade-in slide-in-from-top-4"
                aria-live="polite"
              >
                <span className="block text-[12px] font-semibold text-gray-700">
                  {status?.title || (showThinking ? "Tänker..." : null)}
                </span>
                {status?.status && (
                  <span className="block text-[12px] text-gray-500 mt-0.5">{status.status}</span>
                )}
              </div>
            </div>
          </div>
        )}
        <div style={{ height: bottomSpacerPx }} />
      </div>
      {/* Bottom overlay (sits above chat messages) */}
      <div
        ref={bottomBarRef}
        className="max-w-[600px] mx-auto absolute inset-x-0 bottom-0 z-20 pt-3 pb-6"
        style={{
          paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))',
        }}
      >
        {/* Blur background layer */}
        <div
          className="absolute inset-0 backdrop-blur-md -z-10"
          style={{
            background: 'linear-gradient(to bottom, transparent 0%, var(--color-background) 40%, var(--color-background) 100%)',
            maskImage: 'linear-gradient(to bottom, transparent 0%, black 30%, black 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 30%, black 100%)',
          }}
        />
        {((shoppingList?.confirmed && shoppingList?.selected?.length > 1) || showPromptActions) && (
          <div className="px-8 mb-3">
            <div className="flex flex-wrap items-center gap-[8px] justify-start">
              {(shoppingList?.confirmed && shoppingList?.selected?.length > 1) && <ShoppingListButton />}
              {showPromptActions && (
                <div className="flex flex-wrap gap-[8px]">
                  {systemActions!.map((action: any) => (
                    <button
                      key={action.id}
                      onClick={() => { 
                        performGuidedAction?.(action); 
                        setTimeout(scrollLoaderToTop, 0);
                      }}
                      className="px-[20px] py-[12px] bg-white rounded-full border border-gray-200 text-[16px] hover:bg-gray-50 transition-all"
                      style={{ fontFamily: 'var(--font-body)' }}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <SearchInput 
          placeholder={t("chat.placeholder")}
          selectedOptions={[]}
          onRemoveOption={() => {}}
          onSubmit={(val) => handleSubmit(val)}
          disabled={loading || shouldDisableGuided}
          staticPrefix={computedStaticPrefixKey ? t(computedStaticPrefixKey) : undefined}
        />
        <div className="px-8 mt-2 text-center">
          <span className="text-[11px] text-gray-400">
            Powered by{' '}
            <a
              href="https://www.pakolabs.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-500"
            >
              Pako Labs
            </a>
          </span>
        </div>
      </div>
    </div>
  );
} 